import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import {
  decimalInputProps,
  formatNumberInputText,
  getNumberInputDisplayValue,
  numberInputProps,
  parseOptionalFormattedInteger,
  parseOptionalFormattedNumber,
} from '../../utils/numberFormat';

type FormattedNumberTextFieldProps = Omit<TextFieldProps, 'type' | 'value' | 'onChange'> & {
  value: number | null | undefined;
  onValueChange: (value: number | undefined) => void;
  integer?: boolean;
  allowEmpty?: boolean;
};

export const FormattedNumberTextField: React.FC<FormattedNumberTextFieldProps> = ({
  value,
  onValueChange,
  integer = true,
  allowEmpty = false,
  inputProps,
  onFocus,
  onBlur,
  ...textFieldProps
}) => {
  const isFocusedRef = React.useRef(false);
  const [displayValue, setDisplayValue] = React.useState(() =>
    getNumberInputDisplayValue(value, integer ? { maximumFractionDigits: 0 } : undefined)
  );

  React.useEffect(() => {
    if (isFocusedRef.current) return;

    setDisplayValue(
      getNumberInputDisplayValue(value, integer ? { maximumFractionDigits: 0 } : undefined)
    );
  }, [integer, value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatNumberInputText(event.target.value, { integer });
    setDisplayValue(formattedValue);

    const parsedValue = integer
      ? parseOptionalFormattedInteger(formattedValue)
      : parseOptionalFormattedNumber(formattedValue);

    onValueChange(allowEmpty ? parsedValue : (parsedValue ?? 0));
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    isFocusedRef.current = true;
    onFocus?.(event);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    isFocusedRef.current = false;
    const parsedValue = integer
      ? parseOptionalFormattedInteger(displayValue)
      : parseOptionalFormattedNumber(displayValue);

    setDisplayValue(
      getNumberInputDisplayValue(
        allowEmpty ? parsedValue : (parsedValue ?? 0),
        integer ? { maximumFractionDigits: 0 } : undefined
      )
    );
    onBlur?.(event);
  };

  return (
    <TextField
      {...textFieldProps}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputProps={{
        ...(integer ? numberInputProps : decimalInputProps),
        ...inputProps,
      }}
    />
  );
};
